import React, {useEffect, useRef, useState} from 'react';
import Styled from 'styled-components';
import PropTypes from 'prop-types';
import {Label} from '../label';
import {Tooltip} from '../tooltip';

const LabelGroupContainer = Styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: ${props => (props.noWrap ? 'nowrap' : 'wrap')};
	position: relative;
	overflow: hidden;
	margin-right: ${props => (props.noWrap ? '4px' : '8px')};
	width: 100%;
	column-gap: 4px;
`;

const Dots = Styled.div`
	font-size: 16px;
`;

export const LabelGroup = ({labels, overflowStyle, onLabelRemove}) => {
	const [isOverflowing, setIsOverflowing] = useState(false);

	const containerRef = useRef(null);

	const updateOverflowing = labelGroupContainer => {
		if (labelGroupContainer) {
			const overflowing = labelGroupContainer.scrollWidth > labelGroupContainer.clientWidth;

			if (overflowing !== isOverflowing) {
				setIsOverflowing(overflowing);
			}
		}
	};

	useEffect(() => {
		updateOverflowing(containerRef.current);
	}, [labels]);

	const createLabelNames = labels => labels.map(label => label.name).join(', ');

	const createLabelGroupContainer = noWrap => {
		return (
			<LabelGroupContainer noWrap={noWrap} ref={lg => (containerRef.current = lg)}>
				{labels.map((label, index) => (
					<Label key={index} color={label.color} name={label.name} onRemove={onLabelRemove} />
				))}
			</LabelGroupContainer>
		);
	};

	switch (overflowStyle) {
		case 'TOOLTIP':
			return (
				<Tooltip spanStyle={{width: '100%', display: 'flex'}} content={createLabelNames(labels)}>
					{createLabelGroupContainer(true)}
					{isOverflowing ? <Dots>...</Dots> : null}
				</Tooltip>
			);
		case 'WRAP':
		default:
			return createLabelGroupContainer(false);
	}
};

LabelGroup.propTypes = {
	/**
	 * An array of label definitions
	 */
	labels: PropTypes.arrayOf(
		PropTypes.shape({
			/**
			 * The name of the label, which is also displayed as text
			 */
			name: PropTypes.string.isRequired,

			/**
			 * The color of the label. Text color will be the inverse of this
			 */
			color: PropTypes.string.isRequired,
		})
	).isRequired,

	/**
	 * The style used to wrap the group if it overflows.
	 */
	overflowStyle: PropTypes.oneOf(['TOOLTIP', 'WRAP']),

	/**
	 * Function to be called when a label remove icon is clicked. If no function is provided, then the icon won't be displayed
	 */
	onLabelRemove: PropTypes.func,
};

LabelGroup.defaultProps = {
	labels: [],
	overflowStyle: 'WRAP',
	onLabelRemove: null,
};
